import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, type CanActivateChildFn } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { OrgContextService, isTeamOnlyOrg } from '../../../shared/org-context.service';

/**
 * `orgs/:orgId` 아래 **단체 관리 영역**(개요·구성원·학급구조·권한 등) 진입 가드 — team으로만 연결된
 * 사용자(`isTeamOnlyOrg`: org 직속 role이 null이고 팀 관리 역할도 없는 학생·학부모 등)는 이 영역에 들어올
 * 수 없고 자기 팀 뷰로 보낸다. 팀 admin은 서버가 위임한 팀 관리 화면이 있어 통과한다. 라우트 `data.teamScoped`가 true인 팀 뷰(`teams/:teamId`)는 통과한다.
 *
 * 이 가드는 **화면 틀을 막는 UI 가드일 뿐**이고 실제 인가는 각 API가 한다(서버가 403). 그래서 fail-open이다:
 * 단체 목록을 못 불러왔거나 목록에 없는 단체(방금 만든 단체 등)면 통과시킨다. `role`이 `undefined`
 * (아직 모름)인 경우도 통과한다 — null(team-only)일 때만 막는다.
 */
export const orgAdminAreaGuard: CanActivateChildFn = (childRoute, _state) => {
  if (childRoute.data['teamScoped']) return true;
  const orgId = childRoute.paramMap.get('orgId');
  if (!orgId) return true;

  const orgContext = inject(OrgContextService);
  const router = inject(Router);
  orgContext.ensureLoaded();

  return toObservable(orgContext.loaded).pipe(
    filter(Boolean),
    take(1),
    map(() => {
      const org = orgContext.orgs().find((o) => o.orgId === orgId);
      if (!org || !isTeamOnlyOrg(org)) return true;
      const firstTeam = org.teams?.find((t) => t.teamName.trim() !== '');
      return firstTeam ? router.createUrlTree(['/orgs', orgId, 'teams', firstTeam.teamId]) : router.createUrlTree(['/dashboard']);
    }),
  );
};
